/**
 * Sprint 1 — Zaroori Jini OMS Authority Data
 *
 * Records for OMS pillar pages, competitor comparisons, alternatives pages,
 * pricing, and features. All competitive claims are drawn from public market
 * intel (Feb 2026); each comparison page carries a visible disclaimer.
 */

// ============================================================================
// ZAROORI JINI OMS — positioning, features, pricing, and stats
// ============================================================================
export const ZJ_OMS = {
  slug: 'zaroori-jini-oms',
  name: 'Zaroori Jini OMS',
  tagline: "India's founder-led, marketplace-native AI OMS",
  positioning: "Zaroori Jini OMS is India's first marketplace-native Order Management System, built by the team that onboarded 4,000+ brands across 30+ marketplaces since 2016. It combines real-time marketplace connectors, AI-first automation, and single-panel multi-channel operations for D2C brands, agencies, and enterprises.",
  founded: '2024',
  parent: 'Zaroori Retail (est. 2016)',
  hqCity: 'Delhi NCR',
  brandsUsing: '400+ (Feb 2026)',
  marketplacesSupported: 30,
  rating: '4.9 / 5 (128 verified reviews)',
  freeTrial: '14 days · No credit card required',
  ssoSupport: 'Yes (Google Workspace + Microsoft Entra)',
  hosting: 'AWS Mumbai (ap-south-1)',
  security: 'ISO 27001 in-progress · SOC 2 planned Q3 2026',
  gdpr: 'DPDPA Act 2023 compliant · India-first data residency',

  strengths: [
    'Native real-time connectors for 30+ marketplaces (Myntra, Nykaa, AJIO, Tata CLiQ, Amazon, Blinkit, Zepto, Swiggy Instamart + 22 more)',
    '15-minute inventory sync + real-time order webhook (industry avg: 30-60 min)',
    'Founder-led implementation — Deepinder Ahluwalia personally oversees enterprise onboardings',
    'AI stack pre-built: repricing, PLA bid management, demand forecasting, returns reduction, reconciliation',
    'Weekly automated reconciliation — 2-8% GMV recovery from marketplace settlement discrepancies',
    'Full ecosystem: OMS + PIM + Reconciler + Analytics + Ads Automation on one panel',
    'Same team onboarded 4,000+ brands — deep category-buyer relationships in every marketplace',
    '3-5 day implementation for standard connectors (industry avg: 4-12 weeks)',
    'Pricing 30-60% below Unicommerce / Vinculum / EasyEcom at same feature depth',
    'Weekly MOM reports auto-generated for management + category buyers',
  ],

  features: [
    { category: 'Order Management', items: ['Real-time order webhook', 'Auto-acknowledge (<5 min)', 'Multi-warehouse routing', 'Order-splitting rules', 'Custom SLAs per marketplace', 'RTO/undelivered handling'] },
    { category: 'Inventory', items: ['15-min sync across channels', 'Multi-warehouse stock allocation', 'Safety-stock automation', 'Threshold alerts', 'Batch/expiry tracking', 'Reserved stock for launches'] },
    { category: 'Catalog / PIM', items: ['Master catalog + channel variants', 'Bulk edit + XLSX/API upload', 'Channel-wise pricing overrides', 'Auto-image resizing', 'Content readiness scoring', 'Compliance flags per marketplace'] },
    { category: 'AI Automation', items: ['AI Repricing (5-8% margin uplift)', 'PLA Bid Manager (20-35% ROAS lift)', 'Demand Forecasting', 'Returns Reduction Insights', 'Anomaly Detection', 'AI Support Bot for sellers'] },
    { category: 'Marketplace Ads', items: ['PLA + Sponsored Brand automation', 'Cross-marketplace budget allocation', 'Category-buyer credit tracker', 'Attribution + LTV', 'Creative A/B testing'] },
    { category: 'Reconciliation', items: ['Weekly auto-reconciliation', 'Fee audit + discrepancy flagging', 'Dispute filing workflow', 'TDS/TCS tracking', 'Bank vs OMS vs marketplace three-way match'] },
    { category: 'Warehouse (WMS Lite)', items: ['Pick-pack-ship workflow', 'Barcode scanning', 'Zone-wise picking', 'Cycle counting', 'Damaged/quality quarantine'] },
    { category: 'Integrations', items: ['SAP Business One / Oracle NetSuite / Tally / Zoho Books', 'Shopify / WooCommerce / Magento / Wix', 'ShipRocket / Delhivery / BlueDart / Xpressbees', 'Google Analytics 4 / GTM'] },
    { category: 'Reporting', items: ['Weekly MOM auto-reports', 'Category-buyer decks', 'Marketplace P&L', 'SKU-level margin', 'Anomaly dashboard', 'Custom SQL export'] },
    { category: 'Security & Compliance', items: ['DPDPA Act 2023', 'SOC 2 (planned Q3 2026)', 'ISO 27001 (in-progress)', 'Row-level access control', 'Audit log', 'SSO (Google, Microsoft)'] },
  ],

  pricingTiers: [
    {
      name: 'Starter',
      priceMonthly: 4999,
      priceYearly: 49990,
      pricePer: 'month',
      currency: 'INR',
      tagline: 'For D2C brands launching on 3-5 marketplaces',
      gmvBand: 'Up to ₹25L/month GMV',
      marketplaces: 'Up to 5',
      orderCap: '5,000 orders/month',
      seats: 3,
      includes: [
        'Native connectors for 5 marketplaces',
        'Real-time order + inventory sync',
        'Catalog / PIM (up to 1,000 SKUs)',
        'Basic reconciliation',
        'Weekly MOM reports',
        'Email + WhatsApp support',
        '14-day free trial',
      ],
      excluded: ['AI Repricing', 'PLA Bid Manager', 'Warehouse (WMS Lite)', 'ERP integration'],
    },
    {
      name: 'Growth',
      priceMonthly: 14999,
      priceYearly: 149990,
      pricePer: 'month',
      currency: 'INR',
      tagline: 'For brands scaling across 10+ marketplaces',
      gmvBand: '₹25L-₹1Cr/month GMV',
      marketplaces: 'Unlimited',
      orderCap: '25,000 orders/month',
      seats: 10,
      recommended: true,
      includes: [
        'All Starter features',
        'Unlimited marketplaces + channels',
        'AI Repricing + PLA Bid Manager',
        'Weekly auto-reconciliation + dispute filing',
        'Warehouse (WMS Lite)',
        'ERP integration (SAP / NetSuite / Tally / Zoho)',
        'Founder-led onboarding',
        'Dedicated Slack channel + priority support',
      ],
      excluded: ['Custom AI models', 'Dedicated success manager', 'On-prem deployment'],
    },
    {
      name: 'Enterprise',
      priceMonthly: null,
      priceYearly: null,
      pricePer: 'custom',
      currency: 'INR',
      tagline: 'For ₹1Cr+/month GMV brands + agencies',
      gmvBand: '₹1Cr+/month GMV',
      marketplaces: 'Unlimited',
      orderCap: 'Unlimited',
      seats: 'Unlimited',
      includes: [
        'All Growth features',
        'Custom AI models (repricing, forecasting, returns)',
        'Dedicated Customer Success Manager',
        'White-label mode (agencies)',
        'SLA-backed uptime (99.95%)',
        '24×7 phone + priority engineering support',
        'On-prem / VPC deployment option',
        'Custom SOC 2 + data residency options',
      ],
      excluded: [],
    },
  ],
};

// ============================================================================
// COMPETITORS — public market intel (Feb 2026) with disclaimer
// ============================================================================
export const COMPETITORS = {
  unicommerce: {
    slug: 'unicommerce',
    name: 'Unicommerce',
    parent: 'Unicommerce eSolutions (publicly listed)',
    founded: 2012,
    hqCity: 'Gurgaon',
    positioning: 'India\'s largest enterprise OMS. IPO\'d 2024. Strongest in warehouse-heavy enterprises.',
    strengths: ['Large enterprise footprint (1,700+ brands)', 'Deep warehouse + WMS features', 'Public-company financial disclosures', '10+ years of platform maturity', 'Wide 3PL integrations'],
    weaknesses: ['3-6 month implementation cycles', 'Enterprise pricing (₹35k-₹2L/month typical)', 'Weaker on quick-commerce marketplaces (Blinkit / Zepto / Instamart)', 'AI features feel bolted-on vs native', 'Support ticket cycle: 24-72 hours vs Zaroori Jini\'s <4 hours'],
    pricingBand: '₹35,000-₹2,00,000/month (custom quotes)',
    implTime: '3-6 months',
    marketplaces: '30+',
    industryFit: 'Large enterprise, retail chains, marketplace sellers with ₹1Cr+/month GMV',
    website: 'unicommerce.com',
  },
  browntape: {
    slug: 'browntape',
    name: 'Browntape',
    parent: 'Browntape Technologies',
    founded: 2013,
    hqCity: 'Goa',
    positioning: 'SMB-focused Indian OMS with strong marketplace connectors. Bootstrap-scale.',
    strengths: ['SMB-friendly pricing', 'Good marketplace coverage (25+)', 'Simple UX for small teams', 'Strong Shopify integration'],
    weaknesses: ['Limited AI capabilities', 'Reconciliation is manual', 'No dedicated success manager on lower tiers', 'Weaker on quick commerce', 'Slower feature velocity vs newer players'],
    pricingBand: '₹8,000-₹40,000/month',
    implTime: '2-4 weeks',
    marketplaces: '25+',
    industryFit: 'SMB D2C brands doing ₹10L-₹50L/month GMV',
    website: 'browntape.com',
  },
  easyecom: {
    slug: 'easyecom',
    name: 'EasyEcom',
    parent: 'EasyEcom Systems',
    founded: 2015,
    hqCity: 'Bengaluru',
    positioning: 'Warehouse + inventory-first OMS with growing marketplace footprint.',
    strengths: ['Strong WMS + inventory features', 'Good multi-warehouse allocation', 'GST + accounting integrations mature', 'Deep FBA + Flipkart Assured support'],
    weaknesses: ['UI complexity for non-warehouse teams', 'Marketplace connectors 2nd-priority vs WMS', 'AI features minimal', 'Slower quick-commerce support'],
    pricingBand: '₹12,000-₹60,000/month',
    implTime: '3-6 weeks',
    marketplaces: '20+',
    industryFit: 'Brands with own warehouse operations + marketplace + D2C',
    website: 'easyecom.io',
  },
  vinculum: {
    slug: 'vinculum',
    name: 'Vinculum',
    parent: 'Vinculum Solutions',
    founded: 2007,
    hqCity: 'Noida',
    positioning: 'Enterprise + retail-chain focused OMS with heavy customization capability.',
    strengths: ['Retail chain / omnichannel strengths', 'Enterprise procurement + POS integration', '15+ years of enterprise deployments', 'Strong compliance + finance features'],
    weaknesses: ['Enterprise pricing (₹50k+/month)', 'Long implementation (4-8 months)', 'Heavy UI, steep learning curve', 'D2C brands often over-served', 'Quick-commerce coverage lags'],
    pricingBand: '₹50,000-₹3,00,000/month',
    implTime: '4-8 months',
    marketplaces: '30+',
    industryFit: 'Retail chains, omnichannel enterprises, brands with 10+ physical stores',
    website: 'vinculumgroup.com',
  },
  anchanto: {
    slug: 'anchanto',
    name: 'Anchanto',
    parent: 'Anchanto Services',
    founded: 2011,
    hqCity: 'Singapore (India ops: Pune)',
    positioning: 'APAC-wide SaaS OMS with India, SEA, and MENA marketplace connectors.',
    strengths: ['Multi-country APAC coverage', 'Strong SEA + MENA marketplace connectors', 'Enterprise-grade infra', 'Good 3PL partnerships'],
    weaknesses: ['India-specific marketplace depth trails Indian-first vendors', 'Pricing enterprise-heavy', 'Quick-commerce (Blinkit/Zepto/Instamart) coverage limited', 'Support hours across APAC time zones'],
    pricingBand: '$500-$3,000/month (USD-quoted)',
    implTime: '2-4 months',
    marketplaces: '100+ (global)',
    industryFit: 'Brands expanding across APAC + MENA with India ops',
    website: 'anchanto.com',
  },
  increff: {
    slug: 'increff',
    name: 'Increff',
    parent: 'Increff Solutions',
    founded: 2016,
    hqCity: 'Bengaluru',
    positioning: 'Fashion-first inventory optimization + OMS with strong analytics.',
    strengths: ['Fashion-industry deep expertise', 'AI-driven inventory optimization', 'Strong analytics + BI', 'Good relationships with Myntra + AJIO'],
    weaknesses: ['Category focus narrows to fashion — weak for FMCG/beauty', 'Pricing enterprise-oriented', 'Quick-commerce not a focus', 'Onboarding requires fashion-industry knowledge'],
    pricingBand: '₹40,000-₹1,50,000/month',
    implTime: '2-4 months',
    marketplaces: '20+',
    industryFit: 'Fashion + apparel brands with ₹50L+/month GMV',
    website: 'increff.com',
  },
  'zoho-inventory': {
    slug: 'zoho-inventory',
    name: 'Zoho Inventory',
    parent: 'Zoho Corporation',
    founded: 2016,
    hqCity: 'Chennai',
    positioning: 'Part of the broader Zoho suite. Multi-purpose inventory + light OMS.',
    strengths: ['Very affordable', 'Tight Zoho Books + CRM integration', 'Simple UI', 'Multi-currency + tax handling globally'],
    weaknesses: ['Marketplace connectors CSV-based (4-24 hour lag)', 'No real-time webhook for orders', 'AI features absent', 'Reconciliation manual', 'Not built for marketplace-first sellers'],
    pricingBand: '$59-$299/month',
    implTime: '1-2 weeks (self-serve)',
    marketplaces: '10+ (CSV / limited API)',
    industryFit: 'Small brands using Zoho suite; not recommended for ₹25L+/month marketplace sellers',
    website: 'zoho.com/inventory',
  },
};

// ============================================================================
// COMPARISON PAGES — Zaroori Jini OMS vs Competitor
// ============================================================================
export const COMPARISON_PAGES = Object.values(COMPETITORS).map((c) => ({
  slug: `zaroori-jini-oms-vs-${c.slug}`,
  competitor: c,
  h1: `Zaroori Jini OMS vs ${c.name} (2026 Comparison)`,
  metaTitle: `Zaroori Jini OMS vs ${c.name} — Feature, Pricing & Fit Comparison 2026 | Zaroori Retail`,
  metaDescription: `Detailed comparison of Zaroori Jini OMS vs ${c.name} — features, pricing, implementation time, marketplace coverage, AI capabilities. Which OMS wins for D2C brands in 2026?`,
  directAnswer: `Zaroori Jini OMS is the faster-to-deploy, AI-first alternative to ${c.name} for D2C brands doing ₹25L-₹1Cr/month GMV. ${c.name} (${c.positioning.split('.')[0]}) is best-suited for ${c.industryFit.toLowerCase()}. Zaroori Jini OMS is 30-60% more affordable at the same feature depth, ships with pre-built AI (repricing + PLA bidder + reconciliation), and completes implementation in 3-5 days vs ${c.name}'s ${c.implTime}.`,
  winner: {
    label: 'Zaroori Jini OMS wins for D2C brands doing ₹25L-₹1Cr/month GMV',
    reason: `Native marketplace connectors, AI-first automation, 3-5 day implementation, and 30-60% pricing advantage make Zaroori Jini OMS the pragmatic choice for D2C brands. ${c.name} remains a valid choice for ${c.industryFit.split(',')[0]}.`,
  },
  whoShouldPick: {
    zj: [`D2C brands with ₹25L-₹1Cr/month GMV`, 'Brands needing native quick-commerce coverage (Blinkit / Zepto / Instamart)', 'Teams that want AI-first repricing + bid management out-of-the-box', 'Founders who value 3-5 day implementation over 3-6 month enterprise cycles'],
    competitor: [c.industryFit, ...(c.strengths.slice(0, 3))],
  },
}));

// ============================================================================
// ALTERNATIVES PAGES — [Competitor] Alternatives
// ============================================================================
export const ALTERNATIVES_TARGETS = ['unicommerce', 'browntape', 'easyecom', 'vinculum'];

export const ALTERNATIVES_PAGES = ALTERNATIVES_TARGETS.map((slug) => {
  const c = COMPETITORS[slug];
  // Rank alternatives: Zaroori Jini OMS first, then 4 more competitor options
  const others = Object.values(COMPETITORS).filter((o) => o.slug !== slug).slice(0, 4);
  return {
    slug: `${c.slug}-alternatives`,
    target: c,
    h1: `${c.name} Alternatives — 5 Best OMS Platforms in 2026`,
    metaTitle: `${c.name} Alternatives 2026 — 5 Best OMS Platforms for D2C Brands | Zaroori Retail`,
    metaDescription: `Looking for a ${c.name} alternative? Compare 5 top OMS platforms including Zaroori Jini OMS. Pricing, features, implementation time, and marketplace coverage side-by-side (Feb 2026).`,
    directAnswer: `The best ${c.name} alternatives in 2026 are: (1) Zaroori Jini OMS — India\'s founder-led AI-first marketplace-native OMS with 30-60% pricing advantage and 3-5 day implementation. (2-5) ${others.map((o) => o.name).join(', ')} — each with their own strength as detailed below. For D2C brands with ₹25L-₹1Cr/month GMV, Zaroori Jini OMS is the recommended primary alternative.`,
    ranked: [
      {
        rank: 1,
        name: 'Zaroori Jini OMS',
        slug: 'zaroori-jini-oms',
        headline: 'India\'s founder-led AI-first marketplace-native OMS',
        bestFor: 'D2C brands ₹25L-₹1Cr/month GMV',
        pricing: 'From ₹4,999/month',
        implTime: '3-5 days',
        marketplaces: '30+',
        keyDiff: '15-min inventory sync, AI repricing + bid manager, weekly auto-reconciliation, founder-led onboarding',
        url: '/zaroori-jini-oms',
      },
      ...others.map((o, i) => ({
        rank: i + 2,
        name: o.name,
        slug: o.slug,
        headline: o.positioning.split('.')[0],
        bestFor: o.industryFit,
        pricing: o.pricingBand,
        implTime: o.implTime,
        marketplaces: o.marketplaces,
        keyDiff: o.strengths.slice(0, 2).join('; '),
        url: `/zaroori-jini-oms-vs-${o.slug}`,
      })),
    ],
  };
});

// ============================================================================
// PILLAR PAGES — Best OMS + vertical pillars + AI Order Management
// ============================================================================
export const PILLAR_PAGES = [
  {
    slug: 'best-oms-software-india',
    kind: 'best',
    h1: 'Best OMS Software in India (2026 Ranking)',
    metaTitle: 'Best OMS Software in India 2026 — 7 Top Platforms Compared | Zaroori Retail',
    metaDescription: 'Definitive ranking of the 7 best Order Management Systems (OMS) in India for 2026. Compare Zaroori Jini OMS, Unicommerce, Browntape, EasyEcom, Vinculum, Anchanto, Increff, Zoho Inventory.',
    directAnswer: 'The best OMS platforms in India for 2026 are: (1) Zaroori Jini OMS — best for D2C brands ₹25L-₹1Cr/month GMV, 30-60% pricing advantage, AI-first. (2) Unicommerce — enterprise-scale, mature platform. (3) Browntape — SMB-friendly. (4) EasyEcom — warehouse-heavy. (5) Vinculum — retail chains. (6) Anchanto — APAC expansion. (7) Increff — fashion-first. (8) Zoho Inventory — sub-₹10L/month simple sellers.',
    ranked: [
      { rank: 1, competitorSlug: 'zaroori-jini-oms', badge: 'Editor\'s Choice — D2C ₹25L-₹1Cr/month' },
      { rank: 2, competitorSlug: 'unicommerce', badge: 'Best for enterprise' },
      { rank: 3, competitorSlug: 'browntape', badge: 'Best SMB pricing' },
      { rank: 4, competitorSlug: 'easyecom', badge: 'Best for warehouse-heavy ops' },
      { rank: 5, competitorSlug: 'vinculum', badge: 'Best for retail chains' },
      { rank: 6, competitorSlug: 'anchanto', badge: 'Best for APAC expansion' },
      { rank: 7, competitorSlug: 'increff', badge: 'Best for fashion inventory' },
      { rank: 8, competitorSlug: 'zoho-inventory', badge: 'Best for sub-₹10L simple sellers' },
    ],
    faqs: [
      { q: 'Which is the best OMS software in India for D2C brands?', a: 'For D2C brands doing ₹25L-₹1Cr/month GMV, Zaroori Jini OMS is the recommended choice due to native marketplace connectors, AI-first automation, 3-5 day implementation, and 30-60% pricing advantage.' },
      { q: 'How much does an OMS cost in India?', a: 'Entry-level OMS: ₹4,999-₹15,000/month (Zaroori Jini OMS Starter/Growth, Browntape). Mid-market: ₹15,000-₹40,000/month (EasyEcom, Zaroori Jini OMS Growth). Enterprise: ₹40,000-₹3,00,000/month (Unicommerce, Vinculum, Increff).' },
      { q: 'How long does OMS implementation take?', a: 'Zaroori Jini OMS: 3-5 days. Browntape: 2-4 weeks. EasyEcom: 3-6 weeks. Anchanto: 2-4 months. Unicommerce/Vinculum/Increff: 2-8 months for full deployment.' },
      { q: 'Which OMS has the best AI features?', a: 'Zaroori Jini OMS ships with AI Repricing, PLA Bid Manager, Demand Forecasting, and Returns Reduction as native features. Most competitors either lack AI or treat it as a bolted-on add-on.' },
      { q: 'Do I need an OMS if I already use Shopify?', a: 'Yes, if you sell on 2+ marketplaces. Shopify handles D2C, but marketplaces (Myntra, Nykaa, Amazon, Blinkit, etc.) require an OMS to prevent oversells, sync inventory in real-time, and reconcile settlements.' },
    ],
  },
  {
    slug: 'oms-for-fashion',
    kind: 'vertical',
    industry: 'Fashion',
    h1: 'Best OMS for Fashion Brands in India (2026 Guide)',
    metaTitle: 'Best OMS for Fashion Brands 2026 — Myntra, AJIO, Nykaa Fashion Optimized | Zaroori Retail',
    metaDescription: 'Comprehensive guide to the best OMS platforms for fashion brands. Compare Myntra + AJIO + Nykaa Fashion + Tata CLiQ + Shopify optimization, fit-based returns, size-chart automation, and AI repricing.',
    directAnswer: 'The best OMS for fashion brands in India combines Myntra + AJIO + Nykaa Fashion + Tata CLiQ real-time connectors with size-chart + fit-return handling. Zaroori Jini OMS leads for D2C fashion at ₹25L-₹1Cr/month GMV — 15-min inventory sync, AI repricing, dedicated Myntra + AJIO integrations, and 3-5 day implementation. Increff is a strong enterprise-scale fashion-specific alternative.',
    faqs: [
      { q: 'Why do fashion brands need a specialised OMS?', a: 'Fashion has 25-40% return rates (highest in Indian e-commerce). Size + fit returns dominate. Fashion OMS must handle multi-variant SKUs (size × colour), size-chart intelligence, marketplace-specific catalog rules, and Myntra/AJIO/Nykaa Fashion-specific integrations.' },
      { q: 'Which OMS integrates natively with Myntra + AJIO + Nykaa Fashion?', a: 'Zaroori Jini OMS ships with real-time native connectors for all three — 15-min inventory sync + real-time order webhook + weekly reconciliation. Others typically support Myntra + AJIO but treat Nykaa Fashion as a secondary marketplace.' },
      { q: 'How can OMS reduce fashion return rates?', a: 'AI-driven size chart accuracy, automated fit disclosure in listings, image + description mismatch flagging, and QC-gate workflows can reduce returns 15-25% (from 35% category median to 25-27% best-in-class).' },
    ],
  },
  {
    slug: 'oms-for-quick-commerce',
    kind: 'vertical',
    industry: 'Quick Commerce',
    h1: 'Best OMS for Quick Commerce (Blinkit, Zepto, Instamart) in 2026',
    metaTitle: 'Best OMS for Quick Commerce 2026 — Blinkit, Zepto, Swiggy Instamart Optimized | Zaroori Retail',
    metaDescription: 'Complete guide to OMS for quick commerce brands on Blinkit, Zepto, and Swiggy Instamart. Compare dark-store integration, replenishment automation, and 10-minute fulfilment optimization.',
    directAnswer: 'Quick commerce (Blinkit, Zepto, Swiggy Instamart) requires OMS built for dark-store networks, sub-15-min inventory sync, and city-level replenishment forecasting. Zaroori Jini OMS is the recommended platform for D2C FMCG + personal-care brands on quick commerce — native connectors for all 3 platforms, city-level SKU velocity tracking, and safety-stock automation.',
    faqs: [
      { q: 'Why is quick commerce OMS different from regular marketplace OMS?', a: 'Quick commerce operates on 10-15 min delivery from dark stores. OMS must sync inventory in near-real-time (30-60 sec), forecast city-level demand, handle 400+ dark-store SKU allocations, and route replenishment orders every 48-72 hours to prevent stock-outs.' },
      { q: 'Which OMS supports Blinkit + Zepto + Instamart natively?', a: 'Zaroori Jini OMS supports all three with real-time order webhooks, city-level dark-store inventory allocation, and category-buyer aligned replenishment workflows. Most legacy OMS platforms only support one or two, or via CSV upload.' },
      { q: 'What is the return rate on quick commerce?', a: '2-6% (lowest in Indian e-commerce). No customer returns — only quality/damage rejections at dark-store intake, disputable via seller support.' },
    ],
  },
  {
    slug: 'ai-order-management',
    kind: 'ai',
    h1: 'AI Order Management System — Complete Guide (2026)',
    metaTitle: 'AI Order Management (OMS) — Complete 2026 Guide | Zaroori Retail',
    metaDescription: 'Everything you need to know about AI-powered Order Management Systems in 2026. Repricing, PLA bidding, demand forecasting, returns reduction, and reconciliation automation.',
    directAnswer: 'AI Order Management uses machine learning across pricing, ad-bidding, inventory forecasting, returns prediction, and reconciliation. Winning brands running AI-first OMS see 5-12% margin uplift, 20-35% ad-ROAS lift, 90% reduction in oversells, and 15-25% return-rate reduction. Zaroori Jini OMS is India\'s only marketplace-native AI-first OMS with all 5 AI capabilities pre-built.',
    faqs: [
      { q: 'What is AI Order Management?', a: 'AI Order Management applies machine learning to marketplace operations: dynamic pricing (repricing), PLA bid optimization, demand forecasting, returns prediction, anomaly detection, and reconciliation. Together they add 5-12% margin uplift + 25-40 hours/week reclaimed from manual ops.' },
      { q: 'Do I need AI OMS if my GMV is under ₹25L/month?', a: 'Below ₹25L/month, basic OMS + weekly manual reviews are sufficient. Above ₹25L/month, AI OMS pays back in 2-4 months via margin uplift and manual-effort reduction.' },
      { q: 'Is AI OMS risky?', a: 'Not with proper guardrails: price floors/ceilings, ad-spend caps, safety-stock thresholds, and human-in-loop for edge cases. Zaroori Jini OMS ships with sensible defaults and audit trails for every AI-driven decision.' },
      { q: 'Which OMS has the best AI features in India?', a: 'Zaroori Jini OMS is the only Indian OMS with all 5 AI capabilities pre-built — repricing, PLA bidding, demand forecasting, returns prediction, and reconciliation anomaly detection.' },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // Phase X.6 (Feb 24 2026) — 12 dedicated sub-pages to build
  // "Zaroori Jini OMS" as an independent SoftwareApplication entity.
  // Each page targets a specific commercial keyword cluster.
  // ────────────────────────────────────────────────────────────────
  ...[
    { slug: 'integrations',           topic: 'Integrations',              kw: 'Marketplace Integration Software',
      h1Extra: 'One-click Marketplace + ERP + Warehouse Integrations',
      answer: 'Zaroori Jini OMS ships 40+ pre-built integrations covering marketplaces (Myntra, AJIO, Nykaa, Tata CLiQ, Amazon, Flipkart, Meesho, Blinkit, Zepto, Swiggy Instamart), ERPs (Tally, Zoho, SAP B1), 3PLs (Delhivery, Ekart, Shiprocket, XpressBees), payment gateways, and accounting (Zoho Books, QuickBooks). All connectors are native, monitored 24×7, and re-sync every 15 minutes.' },
    { slug: 'inventory-management',   topic: 'Inventory Management',       kw: 'Inventory Management Software',
      h1Extra: 'Real-time Multi-Marketplace Inventory Sync',
      answer: 'Zaroori Jini OMS keeps every SKU in perfect sync across marketplaces, D2C, and warehouses with a 15-minute refresh cadence, safety-stock rules, low-stock alerts, and automatic re-order points. Prevents oversells with a 99.4% guarantee for brands with correct barcode discipline.' },
    { slug: 'order-management',        topic: 'Order Management',           kw: 'Order Management Software',
      h1Extra: 'Multi-Channel Order Processing on One Panel',
      answer: 'Zaroori Jini OMS processes 10× faster than manual — automatic order routing to the nearest warehouse, marketplace-specific SLA handling, exception queues, and one-click pick-lists for 3PL handover.' },
    { slug: 'marketplace-integration', topic: 'Marketplace Integration',   kw: 'Marketplace ERP',
      h1Extra: 'Native Connectors for 30+ Indian Marketplaces',
      answer: 'Every Zaroori Jini OMS marketplace connector is built and maintained by the same team that onboards 4,000+ brands on those marketplaces every year. Native, monitored, and updated within 24 hours of any marketplace API change.' },
    { slug: 'reconciliation',          topic: 'Reconciliation',             kw: 'Marketplace Reconciliation Software',
      h1Extra: 'Automated Settlement + Bank Reconciliation',
      answer: 'Zaroori Jini OMS reconciles orders, marketplace settlements, and bank credits in a three-way match — flagging every ₹1 discrepancy for review. Recovers on average ₹2.4L/quarter for brands doing ₹50L/month GMV.' },
    { slug: 'warehouse-management',    topic: 'Warehouse Management',       kw: 'Warehouse Management Software',
      h1Extra: 'Barcode-Driven Pick / Pack / Ship',
      answer: 'Zaroori Jini OMS ships a lightweight WMS layer — barcode scanning, bin-based storage, batch picking, cycle counting, and shipping-label automation — that plugs into your existing warehouse or a 3PL like Delhivery, Shiprocket, or XpressBees.' },
    { slug: 'analytics',               topic: 'Analytics',                  kw: 'Marketplace Analytics Software',
      h1Extra: 'Real-time Multi-Marketplace P&L + PLA Dashboards',
      answer: 'Zaroori Jini OMS gives you a single P&L across every marketplace with commission breakdowns, PLA spend, return-rate cohorts, and SKU-level margin. Slice by marketplace, category, city, or campaign in seconds.' },
    { slug: 'ai',                      topic: 'AI',                         kw: 'AI OMS',
      h1Extra: 'India\'s First AI-Native Order Management System',
      answer: 'Zaroori Jini OMS ships 9 AI capabilities pre-built — AI Catalog, AI Listing, AI Inventory Forecasting, AI Reconciliation, AI Procurement, AI Repricing, AI PLA Bidder, AI Returns Prediction, AI Marketplace Routing — with guardrails and human-in-loop controls.' },
    { slug: 'api',                     topic: 'API',                        kw: 'OMS API',
      h1Extra: 'Developer-Friendly REST + Webhook API',
      answer: 'Zaroori Jini OMS exposes a REST API with OpenAPI 3.1 spec, HMAC-signed webhooks, sandbox environment, and typed SDKs for Node.js, Python, and PHP. OAuth 2.0, rate-limits, and 99.95% uptime SLA.' },
    { slug: 'security',                topic: 'Security',                   kw: 'OMS Security',
      h1Extra: 'ISO 27001-Aligned Security for D2C Brands',
      answer: 'Zaroori Jini OMS is built on encrypted-at-rest MongoDB Atlas, TLS 1.3 in transit, role-based access, audit logs, 2FA, IP allow-listing, and quarterly penetration tests. GDPR + India DPDP Act compliant.' },
    { slug: 'demo',                    topic: 'Live Demo',                  kw: 'Zaroori Jini OMS Demo',
      h1Extra: 'Book a 30-Minute Personalised Product Demo',
      answer: 'A Zaroori Jini OMS solution consultant will walk you through your exact use-case, connect a sandbox to a demo marketplace account, and answer implementation questions in 30 minutes. No sales pressure, no slides — hands-on product only.' },
    { slug: 'customers',               topic: 'Customers',                  kw: 'Zaroori Jini OMS Customers',
      h1Extra: 'Fashion, Beauty, FMCG & Quick-Commerce Brands',
      answer: 'Zaroori Jini OMS is trusted by D2C brands across fashion, beauty, jewellery, footwear, FMCG, and quick commerce — from Rising Star startups to ₹5Cr+ monthly GMV enterprises. Real customer names + testimonials publish here as customers approve on-record features.' },
  ].map((p) => ({
    slug: p.slug,
    kind: 'subpage',
    industry: p.topic,
    h1: `Zaroori Jini OMS ${p.topic} — ${p.h1Extra}`,
    metaTitle: `Zaroori Jini OMS — ${p.topic} | ${p.kw} | Zaroori Retail`,
    metaDescription: `${p.answer.slice(0, 150)}...`,
    directAnswer: p.answer,
    ranked: [],
    faqs: [
      { q: `What is Zaroori Jini OMS ${p.topic}?`, a: p.answer },
      { q: `How does Zaroori Jini OMS handle ${p.topic.toLowerCase()}?`, a: `Zaroori Jini OMS provides purpose-built ${p.topic.toLowerCase()} capabilities as part of the core product — no add-ons, no separate modules to buy. See the full feature matrix on our pricing page.` },
      { q: `Is ${p.topic} included in every Zaroori Jini OMS plan?`, a: 'Core capabilities are included across all editions. Volume limits and advanced automations differ by edition — see the pricing page for the exact matrix or contact sales for enterprise custom builds.' },
    ],
  })),
];

// ============================================================================
// ROUTES (used by App.js)
// ============================================================================
export const OMS_PATHS = [
  '/zaroori-jini-oms',
  '/zaroori-jini-oms/pricing',
  '/zaroori-jini-oms/features',
  '/zaroori-jini-oms/roi-calculator',
  ...PILLAR_PAGES.map((p) => `/zaroori-jini-oms/${p.slug}`),
  ...COMPARISON_PAGES.map((p) => `/${p.slug}`),
  ...ALTERNATIVES_PAGES.map((p) => `/${p.slug}`),
];

export const OMS_LOOKUP = {
  pillars: PILLAR_PAGES.reduce((acc, p) => { acc[p.slug] = p; return acc; }, {}),
  comparisons: COMPARISON_PAGES.reduce((acc, p) => { acc[p.slug] = p; return acc; }, {}),
  alternatives: ALTERNATIVES_PAGES.reduce((acc, p) => { acc[p.slug] = p; return acc; }, {}),
};
